import axios from "axios";
import { toast } from "sonner";
import { getImpToken, clearImpersonation } from "@/lib/impersonation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const AUTH_TOKEN_KEY = "ls.auth.token";

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Phase 5 Slice B — attach Bearer header when a localStorage token is present.
// Owner Google OAuth path continues to use the httpOnly cookie; editor login
// path uses Bearer. Server accepts either.
//
// Module 6D Phase 3 — Impersonation override: if a tab-scoped imp_token is
// present in sessionStorage, it WINS over both the localStorage editor token
// AND the studio session cookie. The cookie is suppressed for the request
// (`withCredentials: false`) so the server's cookie-first resolver doesn't
// pick up a stale studio session sitting in the browser jar from another tab.
api.interceptors.request.use((config) => {
  try {
    const impToken = getImpToken();
    if (impToken) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${impToken}`;
      // Suppress cookies so `_parse_token`'s cookie-first path doesn't
      // shadow the imp_token with an unrelated session_token cookie.
      config.withCredentials = false;
      return config;
    }
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${token}`;
    }
  } catch (_) { /* localStorage unavailable — ignore */ }
  return config;
});

// Phase 6 Layer B — global 403 safety net.
//
// Layer A added resource-level role gating; some now-403 endpoints are still
// fetched eagerly by older components that haven't been wired through `can()`.
// A missed per-component guard MUST NOT crash the app — worst case it's a
// quietly empty section, not a full-screen error overlay.
//
// Strategy:
//   1. Tag the rejected error with `silent403 = true` so callers can choose to
//      ignore it without surfacing a generic toast.
//   2. Show one debounced toast per unique endpoint so the user gets a hint
//      something they don't have access to was attempted (but don't spam — a
//      page mounting 6 widgets would otherwise fire 6 toasts).
//   3. Keep the promise rejected so writes still fail loudly at their call
//      site — we are NOT silently turning every 403 into a success.
//
// Combined with the per-component `can()` guards added in Layer B, this is
// belt + suspenders: the guards prevent forbidden fetches from starting,
// the interceptor + window listener catch anything that slips through.
const _toastedPaths = new Set();
let _impToastDebounce = 0;
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    if (status === 403) {
      // Module 6D Phase 4 — Studio suspended: dispatch a global event so
      // AuthProvider can flip into the full-page suspended screen state.
      // Suppress the generic "no access" toast for this code (we want
      // the full-page block, not a quiet toast).
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "studio_suspended") {
        error.silent403 = true;
        try {
          window.dispatchEvent(new CustomEvent("lead_studio:suspended", {
            detail: {
              studio_name:      detail.studio_name,
              reason:           detail.suspended_reason,
              suspended_at:     detail.suspended_at,
              support_email:    detail.support_email,
            },
          }));
        } catch (_) { /* ignore */ }
        return Promise.reject(error);
      }
      // Module 6D Phase 3 — read-only impersonation: server signals with
      // `detail.code === "impersonation_is_read_only"`. Show a distinct
      // toast (not the generic "no access") and DON'T spam — debounce.
      if (detail && typeof detail === "object" && detail.code === "impersonation_is_read_only") {
        error.silent403 = true;
        const now = Date.now();
        if (now - _impToastDebounce > 2_000) {
          _impToastDebounce = now;
          try {
            toast.error("Read-only support session — that action is blocked.", {
              description: "Stop impersonation to act as yourself.",
            });
          } catch (_) { /* sonner not mounted yet */ }
        }
        return Promise.reject(error);
      }
      error.silent403 = true;
      const path = error?.config?.url || "";
      if (!_toastedPaths.has(path)) {
        _toastedPaths.add(path);
        // Auto-clear so a later attempt (after a role change) can re-toast.
        setTimeout(() => _toastedPaths.delete(path), 30_000);
        try {
          toast.error("You don't have access to that section.");
        } catch (_) { /* sonner not mounted yet — ignore */ }
      }
    }
    // Module 6D Phase 3 — 401 on an active impersonation session means
    // it died (30-min TTL, parent logout, etc.). Surface once + clear
    // local context so the banner disappears + future calls don't carry
    // the dead token.
    if (status === 401 && getImpToken()) {
      clearImpersonation();
      try {
        toast.error("Support session ended.", {
          description: "This impersonation expired or was stopped.",
        });
      } catch (_) { /* ignore */ }
    }
    return Promise.reject(error);
  },
);

// Final safety net: even if a call site forgets to await/catch, never let an
// axios 403 surface as an uncaught rejection (would otherwise trip CRA's
// red error overlay in dev). Production crash overlays similarly suppressed.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    if (reason && (reason.silent403 || reason?.response?.status === 403)) {
      ev.preventDefault();
    }
  });
}

export const API_BASE = `${BACKEND_URL}/api`;
